import React, { useEffect, useState } from 'react';
import { Row, Col, Typography, Button, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { UseFormReturn } from 'react-hook-form';

import { FormValue } from '../useForm';
import { MACHINING_CODE_CONDITION } from 'src/constants/quotation/optionSelect';
import useProductMachining from 'src/queries/useProductMachining';
import Modal from 'src/components/custom/Modal';
import { ChamferModal } from 'src/components/common';
import { SCREENS } from 'src/constants/screens';
import { getProductName } from 'src/utils/helper';
import { BLANK_OPTION } from 'src/constants/app';
import { SelectField } from 'src/components/form';

type Props = {
  product: string;
  machiningMethod: string;
  setMachiningMethod: (e) => void;
  formInfo: UseFormReturn<FormValue, any>;
  dataReEstimate?: Response.EstimatePriceDetail['item'];
  isFetchingMachinings: number;
  dataProductDimension?: Response.ProductDimension['item'];
  setIsResetMachiningTolerance: (item: boolean) => void;
  chamferMethod: Response.ChamferItem | null;
  setChamferMethod: (item: Response.ChamferItem | null) => void;
};

const MachiningMethodList: React.FC<Props> = ({
  product,
  machiningMethod,
  setMachiningMethod,
  formInfo,
  dataReEstimate,
  isFetchingMachinings,
  dataProductDimension,
  setIsResetMachiningTolerance,
  chamferMethod,
  setChamferMethod,
}) => {
  const { control, setValue, getValues, watch, clearErrors } = formInfo;

  const varietyCode = watch('variety');
  const shapeCode = watch('shape');

  const [visibleChamferPopup, setVisibleChamferPopup] = useState(false);
  const { t } = useTranslation();
  const { machiningsData: machinings } = useProductMachining(
    product,
    varietyCode,
    shapeCode,
  );

  const [isFilled, setIsFilled] = useState(false);
  const [noData, setNodata] = useState('');

  useEffect(() => {
    // auto fill data machining when ReEstimate from OrderHistory page or back from ProductDetails Page
    if (
      (dataReEstimate?.setdnKakoCd || dataProductDimension?.setdnKakoCd) &&
      !isFilled
    ) {
      if (dataReEstimate?.setdnKakoCd) {
        setMachiningMethod(dataReEstimate?.setdnKakoCd);
        setValue('machiningMethod', dataReEstimate?.setdnKakoCd);
        setMachiningItem(dataReEstimate?.setdnKakoCd);
      }

      if (dataProductDimension?.setdnKakoCd) {
        setMachiningMethod(dataProductDimension?.setdnKakoCd);
        setValue('machiningMethod', dataProductDimension?.setdnKakoCd);
        setMachiningItem(dataProductDimension?.setdnKakoCd);
      }
      if (dataReEstimate?.fraiseKakoPtnm) {
        onSelectChamferMethod({
          fraiseKakoPtnMeis: dataReEstimate?.fraiseKakoPtnm,
          fraiseKakoPtn: dataReEstimate?.fraiseKakoPtn,
          setdnKakoCd: dataReEstimate?.setdnKakoCd,
        });
      }
      if (isFetchingMachinings === 0) {
        setIsFilled(true);
      } else {
        setIsFilled(false);
      }
      return;
    }

    // When a product has a single processing method, always prioritize that processing method.
    if (machinings?.methods?.length === 1) {
      const machiningMethodFirst = _.get(machinings.methods, '[0].value');
      setMachiningMethod(machiningMethodFirst);
      setValue('machiningMethod', machiningMethodFirst);
      setMachiningItem(machiningMethodFirst);
      setValue('isResetByMachiningMethod', true);
      return;
    }
    // set default machining method equal first value
    // const value = _.get(machinings.methods, '[0].value');
    setMachiningMethod(null);
    setMachiningItem(null);
    setValue('machiningMethod', '');
    setNodata(t('pricing_page.empty_data_message'));
  }, [
    _.get(machinings.methods, '[0].value'),
    isFetchingMachinings,
    machinings.methods?.length,
  ]);

  // set machining value when choose chamfer method
  useEffect(() => {
    if (chamferMethod?.setdnKakoCd) {
      const currentMethod = machinings.methods?.find(
        (item) => item.value === chamferMethod?.setdnKakoCd,
      );
      if (currentMethod) {
        setMachiningMethod(currentMethod && currentMethod.value);
        setValue('machiningMethod', currentMethod.value);
      } else {
        setMachiningMethod('');
        setValue('machiningMethod', '');
        setChamferMethod(null);
        setValue('chamferItem', {});
      }
    }
  }, [chamferMethod?.setdnKakoCd]);

  // resset value input sizes when value machining method changed.
  useEffect(() => {
    // recheck visible popup chamfer
    if (machiningMethod !== '') {
      checkVisibleChamferPopup(machiningMethod);
    }
  }, [machiningMethod, product, machinings?.methods]);

  const _onChangeMachining = (value) => {
    // const { value } = e.target;
    setMachiningMethod(value);
    setMachiningItem(value);
    // reset chamfer method value
    setChamferMethod(null);
    setValue('chamferItem', {});
    checkVisibleChamferPopup(value);
    setIsResetMachiningTolerance(true);
    clearErrors('product_width');
    clearErrors('product_length');
    setValue('isResetByMachiningMethod', true);
  };

  const checkVisibleChamferPopup = (value) => {
    const selectedMachiningMethod =
      machinings && machinings?.methods?.find((item) => item.value === value);
    if (
      selectedMachiningMethod &&
      MACHINING_CODE_CONDITION.includes(selectedMachiningMethod.value)
    ) {
      setVisibleChamferPopup(true);
    } else {
      setVisibleChamferPopup(false);
    }
  };

  // set Machining Item by id
  const setMachiningItem = (value) => {
    const machiningItem = machinings?.methods?.find(
      (item) => item.value == value,
    );
    setValue('machiningItem', machiningItem);
  };

  // select chamfer method and set item chamfer to form
  const onSelectChamferMethod = (value) => {
    if (value) {
      setChamferMethod(value);
      setValue('chamferItem', value || {});
      setMachiningItem(value?.setdnKakoCd);
    }
  };

  useEffect(() => {
    if (machinings?.methods?.length === 0) {
      setValue('machiningMethod', _.get(machinings.methods, ''));
      setNodata(t('pricing_page.empty_data_message'));
      return;
    }
  }, [machinings?.methods]);

  return (
    <Row className="machining_row">
      <Col xs={24} sm={4} md={4} xl={4}>
        <Typography.Text className="form_label_required">
          {t('pricing_page.machining_method_field')}
        </Typography.Text>
      </Col>
      <Col xs={24} sm={19} md={19} lg={19} xl={19}>
        <SelectField
          className="w-full"
          selectProps={{
            options: [BLANK_OPTION, ...(machinings?.methods || [])],
            placeholder: noData,
            notFoundContent: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('pricing_page.category_field.no_data')}
              />
            ),
            showSearch: true,
            filterOption: (inputValue, option) => {
              const label = option?.label?.toString().toUpperCase() || '';
              return label.includes(inputValue.toUpperCase());
            },
            optionFilterProp: 'label',
            onSelect: _onChangeMachining,
          }}
          controller={{
            name: 'machiningMethod',
            control,
          }}
        />
      </Col>
      {visibleChamferPopup && (
        <Col
          xs={4}
          sm={4}
          md={4}
          xl={4}
          offset={4}
          className="machining_handle"
        >
          <Modal
            modalProps={{ width: 840 }}
            title={t('chamfer_modal.title')}
            content={({ onClose }) => (
              <ChamferModal
                screenId={SCREENS.R2004}
                onSelect={onSelectChamferMethod}
                productName={getProductName(getValues('productItem'), true)}
                processingMethod={getValues('machiningItem')?.label}
                onClose={onClose}
              />
            )}
          >
            <div className="machining_handle_row">
              {chamferMethod && (
                <span className="chamfer_method">
                  ({chamferMethod?.fraiseKakoPtnMeis}){' '}
                </span>
              )}
              <Button className="machining_handle_button" type="primary">
                {t('pricing_page.machining_method_button')}
              </Button>
            </div>
          </Modal>
        </Col>
      )}
    </Row>
  );
};

export default MachiningMethodList;
